import React, {useState, useEffect} from "react";
import {Box, Paper, Stack, Typography, Button, Tooltip} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {YdTip, HelpPopover, Identity} from "./cells";
import MiniBars from "./MiniBars";
import {fmtSignedMoney, fmtDay, shortProvider, providerCoversNetwork, compareBaseMeta} from "./format";
import {MOVED_ANCHOR_ID, MOVED_VISIBLE, MOVED_LEGEND, MOVED_HELP, movedSummary, usualLine} from "./moved";
import {TYPE, TABULAR, NEUTRAL, ATTENTION_ROW_H} from "./tokens";

const barsWidthFor = (days) => (days > 14 ? 90 : 56);
const keyActivate = (fn) => (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); } };

/** §E grid: tag · what moved / hint · identity · $ · bars · chevron. Same 44 px rhythm as `AttentionRow`. */
const rowSx = (barsWidth, {selected, child, event}) => ({
    display: "grid", gridTemplateColumns: `44px minmax(0,1.1fr) minmax(0,1fr) 60px ${barsWidth + 4}px 16px`, gridTemplateRows: "20px 18px",
    alignItems: "center", columnGap: 1, height: ATTENTION_ROW_H, px: 1.5, pl: child ? 3.5 : 1.5, cursor: "pointer", boxSizing: "border-box",
    borderBottom: `1px solid ${NEUTRAL.hairline}`,
    bgcolor: selected ? NEUTRAL.selected : event ? "rgba(0,0,0,.015)" : "transparent",
    "&:hover": {bgcolor: NEUTRAL.hover},
    "&:focus-visible": {outline: "none", boxShadow: NEUTRAL.focusRing},
});

/** Signed $ — the row's single coloured element, and only when negative. */
const Usd = ({value, sx}) => (
    <Typography component="span" sx={{fontSize: 13, fontWeight: 600, ...TABULAR, textAlign: "right", whiteSpace: "nowrap",
        color: value !== null && value < 0 ? "error.main" : "text.primary", ...sx}}>
        {fmtSignedMoney(value)}
    </Typography>
);

/** `usually $22/d → $30` under the $ (12 secondary; the last figure text.primary). */
const Usual = ({item, compareBase, asOf, sx}) => {
    const u = usualLine(item.ref, item.rev, compareBase, asOf, item.usualN);
    return (
        <Typography component="span" noWrap title={`${u.lead} → ${u.last}`} sx={{fontSize: 12, ...TABULAR, color: "text.secondary", textAlign: "right", minWidth: 0, ...sx}}>
            {u.lead} → <Box component="span" sx={{color: "text.primary"}}>{u.last}</Box>
        </Typography>
    );
};

/** One tag: tag · what moved · site · partner · $ / hint · usually → last; bars span both lines. Hover = the Δd decomposition. */
export const MoverRow = ({item, days, compareBase, asOf, selected, onSelect, child = false}) => {
    const barsWidth = barsWidthFor(days);
    const hint = item.hint.text;
    return (
        <Tooltip title={<YdTip y={{...item.c, day: item.c.day || asOf}} base={compareBase}/>} placement="top" arrow enterDelay={300}>
            <Box role="button" tabIndex={0} aria-label={`${item.tag} moved ${fmtSignedMoney(item.usd)}, open details`}
                 onClick={() => onSelect(item.tag)} onKeyDown={keyActivate(() => onSelect(item.tag))}
                 sx={rowSx(barsWidth, {selected, child})}>
                <Typography component="span" sx={{...TYPE.tag, gridRow: 1, gridColumn: 1}}>{item.tag}</Typography>
                <Typography component="span" noWrap title={item.driver} sx={{gridRow: 1, gridColumn: 2, fontSize: 12.5, fontWeight: 600, color: "text.primary", minWidth: 0}}>
                    {item.driver}
                </Typography>
                {hint && (
                    <Typography component="span" noWrap title={hint} sx={{gridRow: 2, gridColumn: 2, fontSize: 12, color: "text.secondary", minWidth: 0}}>
                        {hint}
                    </Typography>
                )}
                <Identity site={item.site} provider={item.provider} network={item.network} prefix={item.prefix} sx={{gridRow: 1, gridColumn: 3}}/>
                <Usd value={item.usd} sx={{gridRow: 1, gridColumn: 4}}/>
                <Usual item={item} compareBase={compareBase} asOf={asOf} sx={{gridRow: 2, gridColumn: "3 / 5"}}/>
                <Box sx={{gridRow: "1 / 3", gridColumn: 5, display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
                    {!child && <MiniBars daily={item.daily} width={barsWidth} height={18} gap={days > 14 ? 1 : 2} partialLast={item.partialLast} label={`${item.tag}: daily revenue`}/>}
                </Box>
            </Box>
        </Tooltip>
    );
};

/** Tooltip body of a partner event: each tag with its $. */
const EventTip = ({item}) => (
    <Box component="span" sx={{display: "block", ...TABULAR, fontSize: 11.5, lineHeight: 1.35}}>
        <Box component="span" sx={{display: "block", fontWeight: 600}}>{shortProvider(item.provider)} · {item.driver}</Box>
        <Box component="span" sx={{display: "block", opacity: 0.85}}>
            {item.members.map((m, i) => <React.Fragment key={m.tag}>{i > 0 && " · "}{m.tag} {fmtSignedMoney(m.usd)}</React.Fragment>)}
        </Box>
    </Box>
);

/** ≥ 3 tags of one partner moving the same way: one line (`8 tags` · driver · partner · Σ$ · summed bars · chevron); expands to its tags. */
export const EventRow = ({item, days, compareBase, asOf, open, onToggle, onSelect, selectedTag}) => {
    const barsWidth = barsWidthFor(days);
    const showNetwork = Boolean(item.network) && !providerCoversNetwork(item.provider, item.network, item.prefix);
    const identity = `${shortProvider(item.provider)}${showNetwork ? ` · ${item.network}` : ""}`;
    const hint = item.hint.text;
    return (
        <>
            <Tooltip title={<EventTip item={item}/>} placement="top" arrow enterDelay={300}>
                <Box role="button" tabIndex={0} aria-expanded={open}
                     aria-label={`${item.n} tags of ${shortProvider(item.provider)} moved ${fmtSignedMoney(item.usd)}, ${open ? "collapse" : "expand"}`}
                     onClick={onToggle} onKeyDown={keyActivate(onToggle)} sx={rowSx(barsWidth, {event: true})}>
                    <Typography component="span" sx={{...TYPE.tag, fontSize: 11.5, gridRow: 1, gridColumn: 1, whiteSpace: "nowrap"}}>{item.n} tags</Typography>
                    <Typography component="span" noWrap title={item.driver} sx={{gridRow: 1, gridColumn: 2, fontSize: 12.5, fontWeight: 600, color: "text.primary", minWidth: 0}}>
                        {item.driver}
                    </Typography>
                    {hint && (
                        <Typography component="span" noWrap title={hint} sx={{gridRow: 2, gridColumn: 2, fontSize: 12, color: "text.secondary", minWidth: 0}}>
                            {hint}
                        </Typography>
                    )}
                    <Typography component="span" noWrap title={identity} sx={{gridRow: 1, gridColumn: 3, fontSize: 12.5, minWidth: 0}}>
                        <Box component="span" sx={{color: "text.primary", fontWeight: 500}}>{shortProvider(item.provider)}</Box>
                        {showNetwork && <><Box component="span" sx={{color: "text.disabled"}}> · </Box><Box component="span" sx={{color: "text.disabled"}}>{item.network}</Box></>}
                    </Typography>
                    <Usd value={item.usd} sx={{gridRow: 1, gridColumn: 4}}/>
                    <Usual item={item} compareBase={compareBase} asOf={asOf} sx={{gridRow: 2, gridColumn: "3 / 5"}}/>
                    <Box sx={{gridRow: "1 / 3", gridColumn: 5, display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
                        <MiniBars daily={item.daily} width={barsWidth} height={18} gap={days > 14 ? 1 : 2} partialLast={item.partialLast} label={`${item.n} tags: summed daily revenue`}/>
                    </Box>
                    <ExpandMoreIcon sx={{gridRow: "1 / 3", gridColumn: 6, fontSize: 16, color: "text.secondary", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s"}}/>
                </Box>
            </Tooltip>
            {open && item.members.map((m) => (
                <MoverRow key={m.tag} item={m} days={days} compareBase={compareBase} asOf={asOf} selected={selectedTag === m.tag} onSelect={onSelect} child/>
            ))}
        </>
    );
};

/**
 * "Moved yesterday" (brief v2.3): its own paper under Needs attention. Header digest, legend, ≤ 5 items (partner events +
 * single movers, ranked per §D) then `Show N more`. Not an alarm list: no severity colours; the only colour is a negative $.
 */
const MovedPanel = ({items, pendingTags, compareBase, asOf, days, onSelect, selectedTag}) => {
    const [expanded, setExpanded] = useState(false);
    const [openEvents, setOpenEvents] = useState({});
    useEffect(() => { setExpanded(false); }, [items]);
    const {tags, net, events} = movedSummary(items);
    const shown = expanded ? items : items.slice(0, MOVED_VISIBLE);
    const hidden = items.length - MOVED_VISIBLE;
    const base = compareBaseMeta(compareBase);

    return (
        <Paper id={MOVED_ANCHOR_ID} variant="outlined" sx={{borderRadius: 1.5, p: 1.5, pt: 1.25, display: "flex", flexDirection: "column", minWidth: 0, scrollMarginTop: 12}}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{columnGap: 1, rowGap: 0.5}}>
                <Typography component="h2" sx={{...TYPE.eyebrow, textTransform: "uppercase", letterSpacing: "0.04em"}}>Moved yesterday</Typography>
                <Typography component="span" sx={{fontSize: 13, fontWeight: 700, ...TABULAR, color: "text.secondary"}}>{tags}</Typography>
                <Typography component="span" noWrap sx={{fontSize: 12, color: "text.secondary", ...TABULAR, minWidth: 0}}>
                    {items.length > 0 && (
                        <>· <Box component="span" sx={{fontWeight: 600, color: net < -0.05 ? "error.main" : "text.primary"}}>{fmtSignedMoney(net)}</Box> net</>
                    )}
                    {events > 0 && <> · {events} partner event{events === 1 ? "" : "s"}</>}
                    {asOf && <> · {fmtDay(asOf)} vs {base.key === "d1" ? "day before" : "7-day avg"}</>}
                    {pendingTags > 0 && <> · {pendingTags} tags not yet reported</>}
                </Typography>
                <Box sx={{flexGrow: 1}}/>
                <HelpPopover label="Moved yesterday" lines={MOVED_HELP}/>
            </Stack>
            <Typography component="div" noWrap title={MOVED_LEGEND} sx={{fontSize: 11, color: "text.secondary", lineHeight: 1.35, mt: 0.5, mb: 0.75, ...TABULAR}}>{MOVED_LEGEND}</Typography>

            {items.length === 0 ? (
                <Typography sx={{fontSize: 13, color: "text.secondary", py: 1.5, px: 1.5}}>Nothing outside its usual range yesterday.</Typography>
            ) : (
                <Box sx={{mx: -1.5, borderTop: `1px solid ${NEUTRAL.hairline}`}}>
                    {shown.map((it) => (it.kind === "event"
                        ? <EventRow key={it.key} item={it} days={days} compareBase={compareBase} asOf={asOf} open={Boolean(openEvents[it.key])} selectedTag={selectedTag} onSelect={onSelect}
                                    onToggle={() => setOpenEvents((g) => ({...g, [it.key]: !g[it.key]}))}/>
                        : <MoverRow key={it.key} item={it} days={days} compareBase={compareBase} asOf={asOf} selected={selectedTag === it.tag} onSelect={onSelect}/>
                    ))}
                </Box>
            )}
            {hidden > 0 && (
                <Button size="small" onClick={() => setExpanded((e) => !e)} sx={{alignSelf: "flex-start", textTransform: "none", fontSize: 12, mt: 0.25, minHeight: 26, py: 0}}>
                    {expanded ? "Show fewer" : `Show ${hidden} more`}
                </Button>
            )}
        </Paper>
    );
};

export default MovedPanel;
